import { MutationHookOptions, gql, useMutation } from '@apollo/client';
import { useMemo } from 'react';
import { UpdateSavedRowResponse, UpsertSavedRowVariables } from '../types';

export type UpdateSavedRowVariables = Pick<
  UpsertSavedRowVariables,
  'categoryName' | 'categoryId' | 'rowName'
> & {
  rowId: number;
  content?: {
    html: string;
    beeBuilderMetadata: string;
  };
};

export type UpdateSavedRowOptions = MutationHookOptions<
  UpdateSavedRowResponse,
  UpdateSavedRowVariables
>;

const updateSavedRowMutation = gql`
  mutation UpdateBeeRow(
    $rowId: Long!
    $rowName: String
    $categoryId: Long
    $categoryName: String
    $content: BeeRowContentArgsInput
  ) {
    updateBeeRow(
      name: $rowName
      beeRowId: $rowId
      content: $content
      category: { existingCategoryId: $categoryId, createCategoryWithName: $categoryName }
    ) {
      beeRowMetadataId
      beeRowName
      failure {
        ... on BeeRowUpdateFailure {
          errorMsg
        }
        ... on BeeCreateFieldNotProvided {
          field
        }
        ... on BeeCreateFieldTooLongFailure {
          field
        }
        ... on BeeFieldInvalidFailure {
          field
          errorType {
            ... on AnchorTagsWithJS {
              errorMsg
            }
            ... on HasCycle {
              errorMsg
            }
            ... on HasScriptTag {
              errorMsg
            }
            ... on IframeTagWithJS {
              errorMsg
            }
            ... on IllegalCharacters {
              errorMsg
            }
            ... on NotRenderable {
              errorMsg
            }
          }
        }
        ... on BeeRowNameAlreadyExistsFailure {
          name
        }
        ... on BeeSavedRowCreationFailure {
          errorMsg
        }
        ... on MessageCategoryMaximumCountExceededFailure {
          errorMsg
        }
        ... on MessageCategoryNameTooLongFailure {
          maximumLength
        }
      }
    }
  }
`;

export const useUpdateSavedRowMutation = (options?: UpdateSavedRowOptions) => {
  const mutationOptions: UpdateSavedRowOptions = useMemo(
    () => ({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      ...options,
    }),
    [options],
  );

  const [updateSavedRow, { data, loading, reset }] = useMutation<
    UpdateSavedRowResponse,
    UpdateSavedRowVariables
  >(updateSavedRowMutation, mutationOptions);

  return {
    failure: data?.updateBeeRow.failure || null,
    isUpdatingSavedRow: loading,
    updateSavedRow,
    reset,
  };
};
